import React, { useState, useEffect, useRef } from 'react';
import { useDebounce } from '../../hooks/useDebounce.ts';
import { searchGames, getGameStores, getGameDetails, RawgGame, GameStoreInfo } from '../../api/rawgApi.ts';
import './GameSearchInput.css';

export interface GameVariant {
    id?: number;
    name: string;
    image: string | null;
    storeUrl: string | null;
    description?: string;
    released?: string;
    rating?: number;
    metacritic?: number;
    stores?: GameStoreInfo[];
    platforms?: Array<{ platform: { id: number; name: string; slug: string } }>;
}

interface GameSearchInputProps {
    onGameSelect: (game: GameVariant) => void;
    disabled: boolean;
    defaultValue?: string; // Добавляем возможность передать значение по умолчанию
}

const GameSearchInput: React.FC<GameSearchInputProps> = ({ onGameSelect, disabled, defaultValue = '' }) => {
    const [query, setQuery] = useState(defaultValue);
    const [results, setResults] = useState<RawgGame[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [showResults, setShowResults] = useState(false);
    const debouncedQuery = useDebounce(query, 1000); // Увеличиваем с 500 до 1000ms
    const [hasSelected, setHasSelected] = useState(!!defaultValue);
    const inputRef = useRef<HTMLInputElement>(null);

    // Применяем значение по умолчанию при изменении disabled или defaultValue
    useEffect(() => {
        console.log("GameSearchInput: disabled =", disabled, "defaultValue =", defaultValue);
        if (defaultValue) {
            setQuery(defaultValue);
            setHasSelected(true);
            console.log("GameSearchInput: установлено значение по умолчанию:", defaultValue);
        }
    }, [disabled, defaultValue]);

    // Новый эффект для принудительной блокировки ввода при disabled=true
    useEffect(() => {
        if (disabled) {
            setHasSelected(true);
            console.log("GameSearchInput: форма заблокирована");
        }
    }, [disabled]);

    // Упрощаем эффект поиска
    useEffect(() => {
        if (debouncedQuery && !hasSelected && !disabled) {
            const performSearch = async () => {
                setIsLoading(true);
                setShowResults(true);
                try {
                    const games = await searchGames(debouncedQuery);
                    setResults(games);
                } catch (error) {
                    console.error("Error searching games:", error);
                } finally {
                    setIsLoading(false);
                }
            };

            performSearch();
        } else if (!debouncedQuery) {
            setResults([]);
            setShowResults(false);
        }
    }, [debouncedQuery, hasSelected, disabled]);

    const handleSelect = async (game: RawgGame) => {
        setIsLoading(true);
        setHasSelected(true);

        try {
            console.log("Получаем информацию о выбранной игре:", game.name);
            const [gameDetails, stores] = await Promise.all([
                getGameDetails(game.id),
                getGameStores(game.id),
            ]);

            const storeUrl = stores.length > 0
                ? (stores.find(s => s.store.id === 1)?.url || stores[0].url)
                : null;

            const selectedGame: GameVariant = {
                id: game.id,
                name: game.name,
                image: game.background_image,
                storeUrl: storeUrl,
                description: gameDetails?.description || game.description || "",
                released: gameDetails?.released || game.released,
                rating: gameDetails?.rating || game.rating,
                metacritic: gameDetails?.metacritic || game.metacritic,
                stores: stores,
                platforms: gameDetails?.platforms || game.platforms || [],
            };

            onGameSelect(selectedGame);

            setQuery(game.name);
            setResults([]);
            setShowResults(false);
        } catch (error) {
            console.error("Ошибка при получении детальной информации об игре:", error);
            onGameSelect({
                id: game.id,
                name: game.name,
                image: game.background_image,
                storeUrl: null
            });
        } finally {
            setIsLoading(false);
        }
    };

    // Улучшаем обработчик изменения инпута
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (disabled) return;

        const newQuery = e.target.value;
        setQuery(newQuery);

        if (newQuery !== query) {
            setHasSelected(false);
        }

        // Убираем сохранение позиции курсора, так как это вызывает проблемы
        // Браузер сам справится с этим
    };

    // Обработчик клика вне компонента для скрытия результатов
    const wrapperRef = React.useRef<HTMLDivElement>(null);

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
                setShowResults(false);
            }
        }

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [wrapperRef]);

    // Показываем результаты при фокусе, если есть запрос
    const handleFocus = () => {
        // Если компонент заблокирован, не показываем результаты
        if (disabled) return;

        if (query && results.length > 0 && !disabled) {
            setShowResults(true);
        }
    };

    return (
        <div className="game-search-container" ref={wrapperRef}>
            <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={handleInputChange}
                onFocus={handleFocus}
                placeholder="Начните вводить название игры..."
                disabled={disabled}
            />
            {isLoading && <div className="spinner-small"></div>}
            {disabled && <div className="search-disabled-indicator">✓</div>}
            {showResults && !hasSelected && !disabled && results.length > 0 && (
                <ul className="search-results">
                    {results.map(game => (
                        <li key={game.id} onClick={() => handleSelect(game)}>
                            <img src={game.background_image} alt={game.name} className="result-image" />
                            <span>{game.name}</span>
                            {game.metacritic && (
                                <span className="metacritic-score">{game.metacritic}</span>
                            )}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

export default GameSearchInput;
